import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/Modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { ButtonShadcn as Button } from "@/components/ui/Button";

import RHFInput from "@/components/form/RHFInput";
import RHFSelect from "@/components/form/RHFSelect";
import RHFDatePicker from "@/components/form/RHFDateInput";
import { FORM_INPUT_STYLE } from "@/components/form/fieldStyles";

import { userFormSchema } from "../validation.schema";
import { UserService } from "@/services/userService";
import { RoleService } from "@/services/roleService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { hasAnyPermission } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import { PERMISSIONS } from "@/permissions/constant";
import { useTenantScopeParams } from "@/hooks/useTenantScope";

const AddEditUserModal = ({
    isOpen,
    mode = "add",
    onModelClose,
    onHandleSubmit,
    defaultValues,
    editUserId,
}) => {
    const { user } = useAuth();

    // The role and manager dropdowns must come from the tenant this user
    // belongs to — the one the page is scoped to. Unscoped, a SuperAdmin got
    // every shared tenant's roles at once, each name repeated per tenant,
    // and no roles at all for a dedicated tenant. Empty for a tenant user:
    // their JWT already scopes the reply, and the API rejects `tenantId`
    // from anyone but a SuperAdmin.
    const { scopeParams } = useTenantScopeParams();

    // React Hook Form setup with validation
    const methods = useForm({
        resolver: zodResolver(userFormSchema),
        mode: "onChange",
        defaultValues,
    });

    const hasSuperAdmin = hasAnyPermission([PERMISSIONS.SUPER_ADMIN_MANAGE], user.permissions)

    const {
        handleSubmit,
        formState: { isSubmitting },
        watch,
        setValue,
        getValues,
        reset,
    } = methods;

    // Watch selected role to load managers dynamically
    const selectedParentRole = watch("roleId");

    // Role dropdown options
    const [roleOptions, setRoleOptions] = useState([]);
    const [isRoleLoading, setIsRoleLoading] = useState(false);
    // Full role list kept so we can look up the selected role's parentRoleId
    // when populating the Manager dropdown — a user's manager must hold the
    // parent role, not the same role.
    const [rolesById, setRolesById] = useState({});

    // Manager dropdown options
    const [managerOptions, setManagerOptions] = useState([]);
    const [managerLoading, setManagerLoading] = useState(false);
    // True once we've resolved the manager pool at least once for the
    // current role selection — lets us show "no eligible manager" copy
    // instead of just a perpetually-disabled empty dropdown.
    const [managerPoolResolved, setManagerPoolResolved] = useState(false);

    // Tracks the role we last resolved a manager pool for. Seeded from
    // defaultValues so the FIRST effect run (before roles have loaded, on
    // modal open) is never mistaken for the user changing the role.
    const prevRoleRef = useRef(defaultValues?.roleId ?? "");
    const rolesLoadedRef = useRef(false);

    // Reset form + load roles when modal opens
    useEffect(() => {
        if (!isOpen) return;

        reset(defaultValues);
        prevRoleRef.current = defaultValues?.roleId ?? "";
        rolesLoadedRef.current = false;
        setRolesById({});
        setManagerPoolResolved(false);
        fetchRoleList();
    }, [isOpen, defaultValues, reset, scopeParams]);

    // Fetch all available roles for dropdown
    const fetchRoleList = async () => {
        try {
            setIsRoleLoading(true);

            const rolesRes = await RoleService.fetchRolesList({
                pagination: false,
                ...scopeParams,
            });

            const roles = rolesRes?.data?.roles ?? [];

            const roleOptions = roles.map((r) => ({
                label: r.name,
                value: r.id,
            }));

            // Index by id so fetchManagers can resolve `parentRoleId` for
            // the currently-selected role without re-querying the API.
            const byId = roles.reduce((acc, r) => {
                acc[r.id] = r;
                return acc;
            }, {});

            setRoleOptions(roleOptions);
            rolesLoadedRef.current = true;
            setRolesById(byId);
        } catch (e) {
            console.error(e);
            const errorMessage = e?.response?.data?.message ?? e?.response?.data ?? "Failed to load form data";
            toast.error(errorMessage);
        } finally {
            setIsRoleLoading(false);
        }
    };

    // Fetch the managers eligible for the role being assigned.
    //
    // The server's rule is ancestry, not parenthood: any user whose role sits
    // above this one in the tree may manage the user, however far above, and
    // `GET /users/eligible-managers` answers exactly that. This used to ask
    // for the selected role's immediate parent and list the users holding that
    // one role, so on Admin → Lead → Recruiter with nobody holding Lead the
    // dropdown came back empty and the Admin who could have managed them was
    // never offered.
    //
    // `preserveCurrentValue` keeps whatever is already in managerId (e.g.
    // loaded from defaultValues on Edit) as long as it's still a valid option —
    // it's false whenever the role was actively changed by the user, in which
    // case the old manager is never carried over, even if the new pool happens
    // to be empty.
    const fetchManagers = async (roleId, preserveCurrentValue) => {
        try {
            setManagerLoading(true);
            setManagerPoolResolved(false);

            const res = await UserService.fetchEligibleManagers({
                roleId,
                pagination: false,
                ...scopeParams,
            });

            const managers = res?.data?.managers ?? [];

            const options = managers
                .filter((m) => m.id !== editUserId)
                .map((m) => ({
                    label: m.name,
                    value: m.id,
                }));

            setManagerOptions(options);

            if (!preserveCurrentValue) {
                setValue("managerId", "", { shouldDirty: true, shouldValidate: true });
            } else {
                const currentManagerId = getValues("managerId");
                const stillValid = options.some((o) => o.value === currentManagerId);
                if (currentManagerId && !stillValid) {
                    setValue("managerId", "", { shouldDirty: true, shouldValidate: true });
                }
            }

        } catch (e) {
            console.error(e);
            toast.error(apiErrorMessageFormat(e, "Failed to load managers"));
        } finally {
            setManagerLoading(false);
            setManagerPoolResolved(true);
        }
    };

    // Reload managers whenever role changes
    // In the role-change effect — all three setValue("managerId","") calls
    useEffect(() => {
        if (!isOpen) return;
        if (!rolesLoadedRef.current) return;
        if (!selectedParentRole) {
            setManagerOptions([]);
            setValue("managerId", "", { shouldDirty: true });
            setManagerPoolResolved(true);
            return;
        }

        const originalRoleId = defaultValues?.roleId;
        const preserve = !!(mode === "edit" && originalRoleId && originalRoleId === selectedParentRole);

        if (!preserve) {
            setValue("managerId", "", { shouldDirty: true });
        }

        // The role being assigned, not its parent — the route walks the tree.
        fetchManagers(selectedParentRole, preserve);
    }, [isOpen, selectedParentRole, rolesById, setValue, defaultValues, mode, editUserId, scopeParams]);

    // No hint for the top-of-tree role: nothing sits above it, so "nobody is
    // eligible" is the definition of that role rather than news.
    const selectedRoleHasParent = useMemo(() => {
        const r = rolesById[selectedParentRole];
        return Boolean(r?.parentRoleId ?? r?.parentRole?.id);
    }, [rolesById, selectedParentRole]);

    const showNoEligibleManagerHint =
        selectedParentRole &&
        selectedRoleHasParent &&
        managerPoolResolved &&
        !managerLoading &&
        managerOptions.length === 0;

    const managerSelectOptions = useMemo(
        () => [{ label: "No manager", value: "" }, ...managerOptions],
        [managerOptions],
    );

    const onSubmit = (data) => {
        onHandleSubmit({
            ...data,
            managerId: data.managerId || null,
        });
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) onModelClose?.();
            }}
        >
            <DialogContent className="w-[90%] md:w-full flex flex-col">

                {/* Modal Header */}
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <div>
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            {mode === "edit" ? "Edit User" : "Add User"}
                        </DialogTitle>

                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            {mode === "edit"
                                ? "Update user details"
                                : "Create a new user"}
                        </DialogDescription>
                    </div>

                    {/* Close button */}
                    <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
                        <X size={16} strokeWidth={3} />
                    </DialogClose>
                </DialogHeader>

                {/* Form body */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="px-5 py-3">

                                {/* Form fields grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                                    {/* Name */}
                                    <RHFInput
                                        name="name"
                                        label="Name"
                                        placeholder="e.g. Ava Johnson"
                                        inputClass={FORM_INPUT_STYLE}
                                        required
                                        disabled={hasSuperAdmin}
                                    />

                                    {/* Username */}
                                    <RHFInput
                                        name="username"
                                        label="Username"
                                        placeholder="e.g. ava"
                                        inputClass={FORM_INPUT_STYLE}
                                        required
                                        disabled={hasSuperAdmin}
                                    />

                                    {/* Email */}
                                    <div className="col-span-2">
                                        <RHFInput
                                            name="email"
                                            label="Email"
                                            placeholder="e.g. ava@company.com"
                                            inputClass={FORM_INPUT_STYLE}
                                            required
                                            disabled={mode === "edit"}
                                            readOnly={mode === "edit"}
                                        />
                                    </div>

                                    {/* Role selection */}
                                    <RHFSelect
                                        name="roleId"
                                        label="Role"
                                        options={roleOptions}
                                        placeholder={
                                            isRoleLoading
                                                ? "Loading..."
                                                : "Select role"
                                        }
                                        required
                                    />

                                    {/* Manager selection (depends on role) */}
                                    <div>
                                        <RHFSelect
                                            name="managerId"
                                            label="Manager"
                                            placeholder={
                                                !selectedParentRole
                                                    ? "Select role first"
                                                    : managerLoading
                                                        ? "Loading managers..."
                                                        : "Select manager"
                                            }
                                            options={managerSelectOptions}
                                            disabled={
                                                !selectedParentRole ||
                                                managerLoading ||
                                                managerOptions.length === 0
                                            }
                                        />
                                        {showNoEligibleManagerHint && (
                                            <p className="text-[11px] text-amber-600 mt-1">
                                                No one holds a role above this one yet, so this user will be saved without a manager.
                                            </p>
                                        )}
                                    </div>

                                    {/* End Date */}
                                    <div className="col-span-2">
                                        <RHFDatePicker
                                            name="endDate"
                                            label="End Date"
                                            minDate={new Date()}
                                            displayFormat="dd MMM yyyy"
                                            isClear={true}
                                        />
                                    </div>
                                </div>

                                {/* Action buttons */}
                                <div className="flex justify-end gap-3 mt-6">
                                    <DialogClose
                                        render={
                                            <Button
                                                variant="outline"
                                                className="bg-button-tertiary border-none"
                                                type="button"
                                            >
                                                Cancel
                                            </Button>
                                        }
                                    />

                                    <Button
                                        className="bg-button-secondary text-white"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting
                                            ? "Saving…"
                                            : mode === "edit"
                                                ? "Update User"
                                                : "Add User"}
                                    </Button>
                                </div>

                            </div>
                        </form>
                    </FormProvider>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditUserModal;
